import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CakeCard from '../components/CakeCard';
import FeaturedSection from '../components/FeaturedSection';
import PromiseSection from '../components/PromiseSection';
import { fetchCakes, fetchCategories, filterCakesByCategory, getCachedCakes, getCachedCategories } from '../data/cakes';
import { api } from '../utils/api';
import './Home.css';
import happyNewYearImg from '../assets/happy_new_year.png';

const Home = () => {
  const navigate = useNavigate();
  
  // Initialize with cached data immediately for instant render
  const cachedCakesInit = getCachedCakes();
  const cachedCategoriesInit = getCachedCategories();
  
  const [cakes, setCakes] = useState(cachedCakesInit || []);
  const [categories, setCategories] = useState(() => {
    if (cachedCategoriesInit) {
      // Ensure cached categories are sorted immediately
      return [...cachedCategoriesInit].sort((a, b) => b.id - a.id);
    }
    return [];
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  // Only show loading if we don't have cached data
  const [loading, setLoading] = useState(!cachedCakesInit || !cachedCategoriesInit);

  useEffect(() => {
    loadCakes();
  }, []);

  const [debugError, setDebugError] = useState(null);

  // ... (loadCakes function remains same, lines 26-80)

  // Derived state for filtered cakes
  const [sortOrder, setSortOrder] = useState('default');

  // Derived state for filtered cakes
  const filteredCakes = [...cakes]
    .filter(cake => 
      cake.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortOrder === 'default') return 0;
      const priceA = (a.base_price || 0) + (a.custom_design_fee || 0);
      const priceB = (b.base_price || 0) + (b.custom_design_fee || 0);
      return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
    });

  const loadCakes = async () => {
    setDebugError(null);
    
    // 1. Try to load from cache first for instant render
    const cachedCakes = getCachedCakes();
    const cachedCategories = getCachedCategories();
    
    if (cachedCakes && cachedCategories) {
      console.log('Loaded from cache');
      setCakes(cachedCakes);
      // Ensure cached categories are sorted (in case of old cache)
      const sortedCached = [...cachedCategories].sort((a, b) => b.id - a.id);
      setCategories(sortedCached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    
    // 2. Fetch fresh data in background
    try {
      const data = await fetchCakes();
      console.log('Cakes loaded:', data);
      
      if (!data || data.length === 0) {
        // Fetch raw response to see what we're getting
        try {
          const rawData = await api.get('/cakes.php');
          setDebugError('No cakes found.\n\nRaw Response:\n' + JSON.stringify(rawData, null, 2).substring(0, 1000));
        } catch (e) {
          if (!cachedCakes) {
             setDebugError('No cakes found. Failed to get raw response: ' + e.message);
          }
        }
      } else {
        setCakes(data);
      }
      
      // Fetch categories from API (includes subcategories)
      const cats = await fetchCategories();
      if (cats && cats.length > 0) {
        // Sort by ID descending (Recent first)
        const sortedCats = [...cats].sort((a, b) => b.id - a.id);
        setCategories(sortedCats);
      }
      
    } catch (err) {
      console.error('Error loading cakes:', err);
      // Only show error if we don't have cached data
      if (!cachedCakes) {
        setDebugError('Error: ' + (err.message || JSON.stringify(err)));
        setCakes([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleAddToCart = (cake) => {
    navigate(`/cake/${cake.id}`);
  };

  return (
    <div className="home-page">
      {debugError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: 'rgba(255,0,0,0.9)',
          color: 'white',
          padding: '60px 20px 20px',
          zIndex: 9999,
          fontSize: '11px',
          maxHeight: '50vh',
          overflow: 'auto',
          wordBreak: 'break-word'
        }}>
          <h3 style={{margin: '0 0 10px 0'}}>Debug Error:</h3>
          <pre style={{margin: 0, whiteSpace: 'pre-wrap'}}>{debugError}</pre>
          <button onClick={loadCakes} style={{padding: '10px', marginTop: '10px', color: 'black'}}>Retry</button>
          <button onClick={() => setDebugError(null)} style={{padding: '10px', marginTop: '10px', marginLeft: '10px', color: 'black'}}>Close</button>
        </div>
      )}
      <Header title="Supple Bakes" />
      
      {/* Hero Section - Hide when searching */}
      {!searchTerm && (
        <section className="hero gradient-primary">
          <div className="hero-content">
            <div style={{ maxWidth: '200px', margin: '0 auto 1rem' }}>
              <img src={happyNewYearImg} alt="Happy New Year" style={{ width: '100%', height: 'auto', display: 'block' }} />
            </div>
            <h2 className="hero-title fade-in">Freshly Baked, Made with Love</h2>
            <p className="hero-subtitle fade-in">Order your favorite cakes and treats online</p>
          </div>
          
          {/* Instant Delivery Banner */}
          {sortOrder === 'default' && (
            <div className="instant-delivery-strip fade-in" style={{ 
              borderRadius: '12px', 
              maxWidth: '96%',
            }}>
              <div className="ids-content">
                <div className="ids-left">
                  <div className="ids-icon-box" style={{ background: 'rgba(255, 215, 0, 0.2)' }}>
                    <span className="ids-icon">⚡</span>
                  </div>
                  <div className="ids-text" style={{ textAlign: 'left' }}>
                    <span className="ids-title" style={{ fontSize: '0.9rem' }}>Instant Delivery</span>
                    <span className="ids-subtitle" style={{ color: '#eee' }}>Get fresh cakes in 60 mins</span>
                  </div>
                </div>
              
              </div>
            </div>
          )}
        </section>
      )}

      {/* Search Bar */}
      <div className="search-section" style={{ padding: '1rem 0', background: '#fff' }}>
        <div className="container">
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search cakes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 20px',
                paddingLeft: '45px',
                borderRadius: '30px',
                border: '1px solid #ddd',
                fontSize: '16px',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              className="search-input"
            />
            <svg
              style={{
                position: 'absolute',
                left: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9c6b5f'
              }}
              width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                ×
              </button>
            )}
          </div>

          <div style={{ marginTop: '0.8rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 5px' }}>
            <span style={{ fontSize: '0.9rem', color: '#666' }}>
               {filteredCakes.length} Results
            </span>
            <div style={{ position: 'relative' }}>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{
                  appearance: 'none',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                  borderRadius: '20px',
                  padding: '8px 32px 8px 16px',
                  fontSize: '13px',
                  color: '#555',
                  cursor: 'pointer',
                  outline: 'none',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              >
                <option value="default">Sort by: Default</option>
                <option value="asc">Price: Low to High</option>
                <option value="desc">Price: High to Low</option>
              </select>
              <svg 
                style={{ 
                  position: 'absolute', 
                  right: '10px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#999'
                }} 
                width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>



      {/* Categories - Hide when searching */}
      {!searchTerm && (
        <section className="categories-section">
          <div className="containe">
            {/* Main Categories */}
            <div className="categories-scroll">

              {categories.map(cat => (
                 <button
                    key={cat.id}
                    className={`category-bubble-btn ${selectedCategory === parseInt(cat.id) ? 'active' : ''}`}
                    onClick={() => navigate(`/category/${cat.id}`)}
                  >
                    <div className={`bubble-img-container ${!cat.image ? 'no-image' : ''}`}>
                      {cat.image ? (
                        <img 
                          src={`https://panel.supplebakes.com/${cat.image}`} 
                          alt={cat.name}
                          className="bubble-img"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentNode.classList.add('image-error');
                          }}
                        />
                      ) : (
                        <span className="fallback-text">{cat.name.charAt(0)}</span>
                      )}
                    </div>
                    <span className="bubble-label">{cat.name}</span>
                  </button>
              ))}
            </div>
          </div>
        </section>
      )}






      {/* Featured Categories (Now below navigation) */}
      {!searchTerm && !loading && cakes.length > 0 && selectedCategory === 'all' && categories.filter(c => c.is_featured == 1 || c.is_featured === '1' || c.is_featured === true).map(category => {
        let categoryCakes = cakes.filter(c => 
          c.category_id === category.id || 
          (category.subcategories && category.subcategories.some(sub => sub.id === c.category_id))
        );

        // Apply sorting to featured section cakes
        if (sortOrder !== 'default') {
          categoryCakes = [...categoryCakes].sort((a, b) => {
            const priceA = (a.base_price || 0) + (a.custom_design_fee || 0);
            const priceB = (b.base_price || 0) + (b.custom_design_fee || 0);
            return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
          });
        }

        if (categoryCakes.length === 0) return null;

        return (
          <FeaturedSection 
            key={category.id}
            title={category.name} 
            subtitle="Freshly baked with love" 
            cakes={categoryCakes} 
            onCakeClick={handleAddToCart}
            background="linear-gradient(to bottom, #fffdf5, #ffffff)"
            badgeText="Exclusive Collection"
          />
        );
      })}

      {/* Featured Sections - Original Location (All populated categories) */}
      {!searchTerm && !loading && cakes.length > 0 && selectedCategory === 'all' && categories.map(category => {
        let categoryCakes = cakes.filter(c => 
          c.category_id === category.id || 
          (category.subcategories && category.subcategories.some(sub => sub.id === c.category_id))
        );

        // Apply sorting to featured section cakes
        if (sortOrder !== 'default') {
          categoryCakes = [...categoryCakes].sort((a, b) => {
            const priceA = (a.base_price || 0) + (a.custom_design_fee || 0);
            const priceB = (b.base_price || 0) + (b.custom_design_fee || 0);
            return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
          });
        }

        if (categoryCakes.length === 0) return null;

        // Skip if this category is already shown in the top "Featured" section
        if (category.is_featured == 1) return null;

        return (
          <FeaturedSection 
            key={category.id}
            title={category.name} 
            subtitle="Freshly baked with love" 
            cakes={categoryCakes} 
            onCakeClick={handleAddToCart}
          />
        );
      })}

      {/* Our Promise Section - Hide when searching */}
      {!searchTerm && <PromiseSection />}

      {/* Products Grid - All Cakes Or Search Results */}
      <section className="products-section">
        <div className="container">
          <h3 className="section-title">
           <h3 className="section-title">
             {searchTerm ? `Search Results (${filteredCakes.length})` : 'All Cakes'}
           </h3>
          </h3>
          
          {loading ? (
            <div className="loading-grid">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton" style={{ height: '200px', marginBottom: '1rem' }}></div>
                  <div className="skeleton" style={{ height: '20px', width: '60%', marginBottom: '0.5rem' }}></div>
                  <div className="skeleton" style={{ height: '16px', width: '100%', marginBottom: '0.5rem' }}></div>
                  <div className="skeleton" style={{ height: '16px', width: '80%' }}></div>
                </div>
              ))}
            </div>
          ) : filteredCakes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>No cakes found</h3>
              {searchTerm && <p>Try searching for something else</p>}
            </div>
          ) : (
            <div className="cakes-grid">
              {filteredCakes.map(cake => (
                <CakeCard 
                  key={cake.id} 
                  cake={cake} 
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;
